import { TraceConfig, ITraceManager } from './types';
import { Trace } from './trace';
import { Span } from './span';
import { Reporter } from './reporter';
import { Executor } from './executor';

/**
 * TraceManager 类 - 管理 Trace 和 Span 的创建、结束和上报
 */
export class TraceManager implements ITraceManager {
  private config: TraceConfig | null = null;
  private currentTrace: Trace | null = null;
  private reporter: Reporter = new Reporter();
  private executor: Executor = this.reporter.getExecutor();
  private traceTimeoutId: number | null = null;
  private readonly DEFAULT_MAX_TRACE_DURATION = 60 * 1000; // 1 分钟
  private sessionId: string = '';
  private traceTimeouts: Map<string, number> = new Map(); // traceId -> timeoutMs

  /**
   * 初始化配置
   */
  init(config: TraceConfig): void {
    this.config = config;
    this.reporter.init(config);

    // 生成 sessionId（如果还没有生成）
    if (!this.sessionId) {
      this.sessionId = this.generateSessionId();
    }

    if (config.debug) {
      console.log('TraceManager initialized:', config);
      console.log('Session ID:', this.sessionId);
    }
  }

  /**
   * 创建新的 Trace
   * @param operationName 操作名称
   * @param timeoutMs 超时时间（毫秒），如果未设置则使用默认的 1 分钟
   */
  createTrace(operationName: string, timeoutMs?: number): Trace | null {
    if (!this.config) {
      console.error('TraceManager not initialized. Call init() first.');
      return null;
    }

    // 如果已有未结束的 trace，先结束它
    if (this.currentTrace && !this.currentTrace.finished) {
      console.warn('Finishing previous trace before creating new one');
      this.finishTrace();
    }

    const trace = new Trace(operationName);
    this.currentTrace = trace;

    // 设置超时时间（使用参数或默认值）
    const traceTimeoutMs = timeoutMs || this.DEFAULT_MAX_TRACE_DURATION;
    this.traceTimeouts.set(trace.traceId, traceTimeoutMs);

    // 自动创建根 span
    const rootSpan = trace.createRootSpan(operationName);

    // 自动添加 sessionId 到根 span
    if (this.sessionId) {
      rootSpan.addTag('session.id', this.sessionId);
    }

    // 设置超时自动结束
    this.setTraceTimeout();

    if (this.config.debug) {
      console.log('Trace created:', trace.traceId, operationName);
      console.log('Session ID:', this.sessionId);
    }

    return trace;
  }

  /**
   * 在当前 Trace 中创建 Span
   */
  createSpan(operationName: string, parentSpan?: Span): Span | null {
    if (!this.config) {
      console.error('TraceManager not initialized. Call init() first.');
      return null;
    }

    if (!this.currentTrace) {
      console.warn('No active trace. Creating a new trace first.');
      const newTrace = this.createTrace(operationName);
      if (newTrace) {
        return newTrace.rootSpan || null;
      }
      return null;
    }

    if (this.currentTrace.finished) {
      console.warn('Current trace is finished. Cannot create span.');
      return null;
    }

    const span = this.currentTrace.createSpan(operationName, parentSpan);

    if (this.config.debug) {
      console.log('Span created:', span.spanId, operationName);
    }

    return span;
  }

  /**
   * 创建一个链式调用的 Span（便捷方法）
   * @param operationName 操作名称
   * @param parentSpan 父 Span（可选）
   * @returns 可链式调用的 Span，如果失败则返回 null
   */
  span(operationName: string, parentSpan?: Span): Span | null {
    return this.createSpan(operationName, parentSpan);
  }

  /**
   * 结束指定的 Span
   */
  finishSpan(span: Span): void {
    if (!this.config) {
      console.error('TraceManager not initialized. Call init() first.');
      return;
    }

    if (span.finished) {
      console.warn('Span already finished:', span.operationName);
      return;
    }

    span.finish();

    if (this.config.debug) {
      console.log('Span finished:', span.spanId, span.operationName, `duration: ${span.duration}ms`);
    }
  }

  /**
   * 结束当前 Trace 并上报数据
   */
  finishTrace(): void {
    if (!this.config) {
      console.error('TraceManager not initialized. Call init() first.');
      return;
    }

    if (!this.currentTrace) {
      console.warn('No active trace to finish.');
      return;
    }

    if (this.currentTrace.finished) {
      console.warn('Trace already finished.');
      this.currentTrace = null;
      return;
    }

    // 结束 trace
    this.currentTrace.finish();

    // 转换为 TraceData 并上报
    const traceData = this.currentTrace.toData(
      this.config.serviceName,
      this.config.serviceInstance || this.config.serviceName,
      this.config.serviceVersion || '1.0.0'
    );

    if (this.config.debug) {
      console.log('Trace finished:', this.currentTrace.traceId, `duration: ${this.currentTrace.duration}ms`);
    }

    // 清除超时
    this.clearTraceTimeout();

    // 异步上报，不阻塞，使用安全执行
    this.executor.safeExecuteAsync(
      () => this.reporter.report(traceData),
      'trace-report'
    );

    // 清理当前 trace 的超时时间映射
    if (this.currentTrace) {
      this.traceTimeouts.delete(this.currentTrace.traceId);
    }

    // 清空当前 trace
    this.currentTrace = null;
  }

  /**
   * 为 Span 添加标签
   */
  setSpanTag(span: Span, key: string, value: string): void {
    if (!this.config) {
      console.error('TraceManager not initialized. Call init() first.');
      return;
    }

    span.addTag(key, value);

    if (this.config.debug) {
      console.log('Span tag added:', span.spanId, key, value);
    }
  }

  /**
   * 为 Span 添加日志
   */
  addSpanLog(span: Span, logData: Record<string, any>): void {
    if (!this.config) {
      console.error('TraceManager not initialized. Call init() first.');
      return;
    }

    span.addLog(logData);

    if (this.config.debug) {
      console.log('Span log added:', span.spanId, logData);
    }
  }

  /**
   * 获取当前 Trace（用于调试）
   */
  getCurrentTrace(): Trace | null {
    return this.currentTrace;
  }

  /**
   * 获取当前 Span（用于调试）
   */
  getCurrentSpan(): Span | undefined {
    return this.currentTrace?.getCurrentSpan();
  }

  /**
   * 获取当前根 Span（便捷方法）
   */
  get rootSpan(): Span | null {
    return this.currentTrace?.rootSpan || null;
  }

  /**
   * 获取当前 Session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  getExecutor(): Executor {
    return this.executor;
  }

  /**
   * 强制执行批量上报
   */
  forceBatchReport(): Promise<void> {
    if (!this.config) {
      console.error('TraceManager not initialized. Call init() first.');
      return Promise.resolve();
    }

    return this.executor.safeExecuteAsync(
      () => this.reporter.forceBatchReport(),
      'force-batch-report'
    ) || Promise.resolve();
  }

  /**
   * 获取缓存中的 Trace 数量
   */
  getCacheSize(): number {
    if (!this.config) {
      return 0;
    }

    return this.reporter.getCacheSize();
  }

  /**
   * 清空缓存
   */
  clearCache(): void {
    if (!this.config) {
      console.error('TraceManager not initialized. Call init() first.');
      return;
    }

    this.reporter.clearCache();
  }

  /**
   * 生成 Session ID
   */
  private generateSessionId(): string {
    // 使用时间戳 + 随机字符串生成唯一的 sessionId
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    return `${timestamp}-${random}`;
  }

  /**
   * 设置 Trace 超时自动结束
   */
  private setTraceTimeout(): void {
    // 清除旧的超时
    this.clearTraceTimeout();

    if (!this.config) {
      return;
    }

    // 获取当前 trace 的超时时间
    const maxDuration = this.currentTrace
      ? this.traceTimeouts.get(this.currentTrace.traceId) || this.DEFAULT_MAX_TRACE_DURATION
      : this.DEFAULT_MAX_TRACE_DURATION;

    // 设置超时
    this.traceTimeoutId = window.setTimeout(() => {
      if (this.currentTrace && !this.currentTrace.finished) {
        const traceName = this.currentTrace.operationName;
        const duration = this.currentTrace.duration;
        
        if (this.config?.debug) {
          console.warn(
            `Trace timeout after ${maxDuration}ms, auto-finishing:`,
            traceName,
            `(duration: ${duration}ms)`
          );
        } else {
          console.warn(`Trace timeout, auto-finishing: ${traceName}`);
        }

        // 标记为超时
        const rootSpan = this.currentTrace.rootSpan;
        if (rootSpan) {
          rootSpan.addTag('trace.timeout', 'true');
          rootSpan.addLog({
            event: 'trace-timeout',
            maxDuration: String(maxDuration),
            actualDuration: String(duration),
            message: 'Trace automatically finished due to timeout'
          });
        }

        // 结束并上报
        this.finishTrace();
      }
      this.traceTimeoutId = null;
    }, maxDuration) as unknown as number;
  }

  /**
   * 清除 Trace 超时
   */
  private clearTraceTimeout(): void {
    if (this.traceTimeoutId !== null) {
      clearTimeout(this.traceTimeoutId);
      this.traceTimeoutId = null;
    }
  }
}

